import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRecipientShow, apiRecipientUpdate, Recipient } from "../lib/api";
import { validateIbanRemote } from "../lib/iban";

function inferRail(r?: Recipient): "sepa" | "swift" {
  const a = r?.account;
  if (!a) return "sepa";
  for (const v of [a.bank_bic, a.routing_number, a.intermediary_bank_name, a.intermediary_bank_bic]) {
    if ((v ?? "").toString().trim() !== "") return "swift";
  }
  return "sepa";
}

export default function RecipientEdit() {
  const { id } = useParams();
  const [r, setR] = useState<Recipient | null>(null);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  // form state
  const [iban, setIban] = useState("");
  const [ibanValid, setIbanValid] = useState<boolean | null>(null);
  const [ibanMsg, setIbanMsg] = useState("");

  const [full_address, setAddress] = useState("");
  const [account_number, setAccount] = useState("");
  const [bank_bic, setBic] = useState("");
  const [routing_number, setRouting] = useState("");
  const [bank_name, setBankName] = useState("");
  const [bank_address, setBankAddress] = useState("");
  const [bank_country, setBankCountry] = useState("");
  const [currency, setCurrency] = useState("");
  const [intermediary_bank_name, setInterName] = useState("");
  const [intermediary_bank_bic,   setInterBic] = useState("");
  const [ibanOpt, setIbanOpt] = useState("");

  useEffect(() => {
    (async () => {
      if (!id) return;
      const data = await apiRecipientShow(Number(id));
      setR(data);
      const a = data.account || {};
      setCurrency(data.currency ?? a.currency ?? "");
      setIban(a.iban ?? "");
      setIbanOpt(a.iban ?? "");
      setAccount(a.account_number ?? "");
      setBic((a.bank_bic ?? "").toUpperCase());
      setRouting(a.routing_number ?? "");
      setBankName(a.bank_name ?? "");
      setBankAddress(a.bank_address ?? "");
      setBankCountry((a.bank_country ?? "").toUpperCase());
      setInterName(a.intermediary_bank_name ?? "");
      setInterBic((a.intermediary_bank_bic ?? "").toUpperCase());
      setAddress(data.full_address ?? "");
    })();
  }, [id]);

  const rail = useMemo(() => inferRail(r ?? undefined), [r]);

  async function checkIban() {
    setIbanMsg("Validating IBAN...");
    const res = await validateIbanRemote(iban);
    if (res.valid) { setIbanValid(true); setIbanMsg("✅ Valid IBAN"); }
    else { setIbanValid(false); setIbanMsg("❌ Invalid IBAN"); }
  }

  async function submit() {
    if (!r) return;
    setSaving(true);
    try {
      if (rail === "sepa") {
        await apiRecipientUpdate(r.id, {
          iban,
          currency: currency.toUpperCase().slice(0,3),
        });
      } else {
        await apiRecipientUpdate(r.id, {
          full_address,
          account_number: account_number || null,
          bank_bic: bank_bic ? bank_bic.toUpperCase() : null,
          routing_number: routing_number || null,
          bank_name: bank_name || null,
          bank_address: bank_address || null,
          bank_country: bank_country.toUpperCase().slice(0,2),
          currency: currency.toUpperCase().slice(0,3),
          iban: ibanOpt || null,
          intermediary_bank_name: intermediary_bank_name || null,
          intermediary_bank_bic: intermediary_bank_bic ? intermediary_bank_bic.toUpperCase() : null,
        });
      }
      nav("/recipients?flash=updated");
    } finally {
      setSaving(false);
    }
  }

  if (!r) return <div className="skeleton">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg p-6 space-y-6">
      {rail === "sepa" ? (
        <>
          <h2 className="text-xl font-bold">Edit SEPA Recipient</h2>
          <div>
            <label className="font-medium text-sm">IBAN</label>
            <input className="w-full border rounded p-2" value={iban} onChange={(e) => setIban(e.target.value)} onBlur={checkIban} />
            {!!iban && <p className={`text-sm mt-1 ${ibanValid ? "text-green-600" : "text-red-600"}`}>{ibanMsg}</p>}
          </div>
          <div>
            <label className="font-medium text-sm">Currency</label>
            <input className="w-full border rounded p-2 bg-gray-100 text-gray-700" value={currency} readOnly />
          </div>
          <button className="btn btn-success mt-2" onClick={submit} disabled={saving || ibanValid === false}>
            Update SEPA Recipient
          </button>
        </>
      ) : (
        <>
          <h2 className="text-xl font-bold">Edit SWIFT Recipient</h2>
          <div className="space-y-3">
            <div>
              <label className="font-medium text-sm">Recipient Name</label>
              <input className="w-full border rounded p-2 bg-gray-100 text-gray-700" value={r.name} readOnly />
            </div>
            <div>
              <label className="font-medium text-sm">Recipient Full Address</label>
              <input className="w-full border rounded p-2" value={full_address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div>
              <label className="font-medium text-sm">IBAN (optional)</label>
              <input className="w-full border rounded p-2" value={ibanOpt} onChange={(e) => setIbanOpt(e.target.value)} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="font-medium text-sm">Account Number</label>
                <input className="w-full border rounded p-2" value={account_number} onChange={(e) => setAccount(e.target.value)} />
              </div>
              <div>
                <label className="font-medium text-sm">Currency</label>
                <input className="w-full border rounded p-2" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0,3))} />
              </div>
              <div>
                <label className="font-medium text-sm">SWIFT/BIC</label>
                <input className="w-full border rounded p-2" value={bank_bic} onChange={(e) => setBic(e.target.value.toUpperCase())} />
              </div>
              <div>
                <label className="font-medium text-sm">Routing Number</label>
                <input className="w-full border rounded p-2" value={routing_number} onChange={(e) => setRouting(e.target.value)} />
              </div>
              <div>
                <label className="font-medium text-sm">Bank Name</label>
                <input className="w-full border rounded p-2" value={bank_name} onChange={(e) => setBankName(e.target.value)} />
              </div>
              <div>
                <label className="font-medium text-sm">Bank Address</label>
                <input className="w-full border rounded p-2" value={bank_address} onChange={(e) => setBankAddress(e.target.value)} />
              </div>
              <div>
                <label className="font-medium text-sm">Bank Country (ISO 2)</label>
                <input className="w-full border rounded p-2" value={bank_country} onChange={(e) => setBankCountry(e.target.value.toUpperCase().slice(0,2))} maxLength={2} />
              </div>
              <div>
                <label className="font-medium text-sm">Intermediary Bank Name</label>
                <input className="w-full border rounded p-2" value={intermediary_bank_name} onChange={(e) => setInterName(e.target.value)} />
              </div>
              <div>
                <label className="font-medium text-sm">Intermediary Bank BIC</label>
                <input className="w-full border rounded p-2" value={intermediary_bank_bic} onChange={(e) => setInterBic(e.target.value.toUpperCase())} />
              </div>
            </div>
          </div>
          <button className="btn btn-success" onClick={submit} disabled={saving}>
            Update SWIFT Recipient
          </button>
        </>
      )}
    </div>
  );
}
